import React, { useState, useEffect } from 'react';
import { Tag, Gauge, Ruler, StickyNote } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { useUpdateReadingTemplate } from '../../hooks/useReadingTemplates';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';
import { normalizeFieldErrors } from '../../services/api';

const EditReadingTemplateModal = ({ isOpen, onClose, template }) => {
    const [formData, setFormData] = useState({
        label: '',
        value: '',
        unit: '',
        notes: '',
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const updateTemplate = useUpdateReadingTemplate();
    const { t } = useLanguage();

    useEffect(() => {
        if (template) {
            setFormData({
                label: template.label || '',
                value: template.value?.toString() ?? '',
                unit: template.unit || '',
                notes: template.notes || '',
            });
        }
    }, [template]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.label.trim()) {
            newErrors.label = t(KEYS.READING_TEMPLATE_FIELD_LABEL_REQUIRED);
        } else if (formData.label.length > 191) {
            newErrors.label = t(KEYS.COMMON_ERROR_MAX_CHARS);
        }

        if (formData.value !== '' && (isNaN(parseFloat(formData.value)) || parseFloat(formData.value) < 0)) {
            newErrors.value = 'Value must be a valid number (0 or greater)';
        }

        if (formData.unit && formData.unit.length > 50) {
            newErrors.unit = 'Maximum 50 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');

        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            const data = {
                label: formData.label.trim(),
                value: formData.value !== '' ? parseFloat(formData.value) : undefined,
                unit: formData.unit || undefined,
                notes: formData.notes || undefined,
            };

            await updateTemplate.mutateAsync({ uid: template.uid, data });

            onClose();
        } catch (error) {
            if (error.code === 'VALIDATION_ERROR' && error.errors) {
                setErrors(normalizeFieldErrors(error.errors));
            } else {
                setSubmitError(error.message || t(KEYS.READING_TEMPLATE_EDIT_ERROR));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setErrors({});
            setSubmitError('');
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t(KEYS.READING_TEMPLATE_EDIT_TITLE)}
            subtitle="Update template details"
            size="medium"
            footer={
                <>
                    <Button variant="outline" size="medium" onClick={handleClose} disabled={isSubmitting}>
                        {t(KEYS.COMMON_CANCEL)}
                    </Button>
                    <Button variant="primary" size="medium" onClick={handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ? t(KEYS.READING_TEMPLATE_EDIT_SUBMITTING) : t(KEYS.READING_TEMPLATE_EDIT_SUBMIT)}
                    </Button>
                </>
            }
        >
            {submitError && (
                <div className="p-3 bg-status-overdue-bg text-status-overdue rounded text-sm mb-4">
                    {submitError}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
                {/* Label */}
                <div className="space-y-1">
                    <label htmlFor="label" className="label-base flex items-center gap-1.5">
                        <Tag className="w-4 h-4" />
                        Label <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        id="label"
                        name="label"
                        value={formData.label}
                        onChange={handleChange}
                        className={`input-base ${errors.label ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="e.g., Monthly Meter Read"
                        maxLength={191}
                        disabled={isSubmitting}
                    />
                    {errors.label && (
                        <span className="text-xs text-red-500 mt-1">{errors.label}</span>
                    )}
                </div>

                {/* Value */}
                <div className="space-y-1">
                    <label htmlFor="value" className="label-base flex items-center gap-1.5">
                        <Gauge className="w-4 h-4" />
                        Default Value
                    </label>
                    <input
                        type="number"
                        id="value"
                        name="value"
                        value={formData.value}
                        onChange={handleChange}
                        className={`input-base ${errors.value ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="e.g., 0.00"
                        step="0.01"
                        min="0"
                        disabled={isSubmitting}
                    />
                    {errors.value && (
                        <span className="text-xs text-red-500 mt-1">{errors.value}</span>
                    )}
                </div>

                {/* Unit */}
                <div className="space-y-1">
                    <label htmlFor="unit" className="label-base flex items-center gap-1.5">
                        <Ruler className="w-4 h-4" />
                        Unit
                    </label>
                    <input
                        type="text"
                        id="unit"
                        name="unit"
                        value={formData.unit}
                        onChange={handleChange}
                        className={`input-base ${errors.unit ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="e.g., kWh, m³, L"
                        maxLength={50}
                        disabled={isSubmitting}
                    />
                    {errors.unit && (
                        <span className="text-xs text-red-500 mt-1">{errors.unit}</span>
                    )}
                </div>

                {/* Notes */}
                <div className="space-y-1">
                    <label htmlFor="notes" className="label-base flex items-center gap-1.5">
                        <StickyNote className="w-4 h-4" />
                        Notes
                    </label>
                    <textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        className="input-base resize-none"
                        rows={3}
                        placeholder="Optional notes..."
                        disabled={isSubmitting}
                    />
                </div>
            </form>
        </Modal>
    );
};

export default EditReadingTemplateModal;
